/* eslint-disable react/forbid-prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Button, Stack, Typography, alpha, CircularProgress,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { useDispatch, useSelector } from 'src/redux/store';
import { getConsentForms } from 'src/redux/slices/consentV2';
import { callApi } from 'src/utils/apiCall';
import useCustomSnackbar from 'src/hooks/useCustomSnackbar';
import useCopyToClipboard from 'src/hooks/use-copy-to-clipboard';
import uuidv4 from 'src/utils/uuidv4';
import { common } from 'src/theme/palette';

import QuickSendStep1Forms from './QuickSendStep1Forms';
import QuickSendStep2Doctor from './QuickSendStep2Doctor';
import QuickSendStep3Patient from './QuickSendStep3Patient';
import QuickSendStep4Deliver from './QuickSendStep4Deliver';
import { buildSendFormsPayload, buildFormUrl } from './quickSendUtils';

const DEFAULT_EXPIRY_DAYS = 7;
const TOTAL_STEPS = 4;

/**
 * QuickSend — 4-step orchestrator for the minimalist Send Forms flow.
 *
 * Owns:
 *   - step (1..4)
 *   - audienceMode (ONE_PATIENT | ANONYMOUS)
 *   - selectedForms
 *   - practiceFieldValues (Treated by goes under `system:treated_by`)
 *   - selectedLead
 *   - activeChannel + expiry + template ids
 *   - the generated form URL (after a successful send-forms POST in Step 3 → 4)
 *
 * Parent (`SendFormsDialog/index.jsx`) wraps the dialog chrome + the
 * Quick / Advanced toggle.
 */
const QuickSend = ({
    open,
    onClose,
    practiceId,
    initialLead,
    // initialForm: kept on PropTypes for API parity with the parent's
    // SendFormsDialog signature, but no longer consumed inside Quick —
    // selectedForms is owned by the parent (B-7).
    // switchToAdvanced: was used by the legacy "Send to multiple patients"
    // link in Step 3. The 3-lane redesign removed that link, but the prop
    // is still threaded by the parent (it's also wired to the toggle in
    // the modal header). Destructured here to silence the warning and
    // make intent explicit.
    // eslint-disable-next-line no-unused-vars
    switchToAdvanced,
    // B-7: state lifted to SendFormsDialog/index.jsx so flipping between
    // Quick and Advanced preserves the user's selections. Quick no longer
    // owns selectedForms / selectedLead / practiceFieldValues — it receives
    // them as controlled props.
    selectedForms,
    onAddForm,
    onRemoveForm,
    onReorderForms,
    selectedLead,
    onSelectLead: onSelectLeadProp,
    practiceFieldValues,
    setPracticeFieldValues,
    onSuccess,
}) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useCustomSnackbar();
    const { copy } = useCopyToClipboard();

    const { forms, formsLoading } = useSelector((state) => state.consentV2);
    const practice = useSelector((state) => state.practice?.practice);

    // ── Wizard state ─────────────────────────────────────────────────────────
    const [step, setStep] = useState(1);
    const [audienceMode, setAudienceMode] = useState('ONE_PATIENT');
    // selectedForms / selectedLead / practiceFieldValues now come from props
    // (B-7 — see comment on the prop destructure above).
    // Default to COPY because Step 4 now leads with a "Link ready" banner +
    // big copy button. EMAIL/WhatsApp are one tap away if the user wants the
    // template-driven dispatch.
    const [activeChannel, setActiveChannel] = useState('COPY');
    const [expiryDays] = useState(DEFAULT_EXPIRY_DAYS);
    const [generating, setGenerating] = useState(false);
    const [sendResult, setSendResult] = useState(null); // { formUrl, links, expiresAt }
    const [consentConfig, setConsentConfig] = useState(null);
    const [sending, setSending] = useState(false);

    const [emailTemplates, setEmailTemplates] = useState([]);
    const [whatsappTemplates, setWhatsappTemplates] = useState([]);
    const [emailTemplateId, setEmailTemplateId] = useState(null);
    const [whatsappTemplateId, setWhatsappTemplateId] = useState(null);

    // High-fidelity PDF toggle — default ON. When ON, the patient's
    // browser-rendered (html2canvas → jsPDF) PDF is persisted at submit
    // time. When OFF, the public submit view ignores the upload and
    // ``apps/practice/pdf_generator.py`` (ReportLab) generates the
    // server-side fallback. Persisted on the ConsentRequest at create
    // time so the pre-flight CR + every channel send agree.
    const [highFidelityPdf, setHighFidelityPdfRaw] = useState(true);

    // B-4: idempotency key minted ONCE per dialog session and reused across
    // every send-forms POST in this flow. Without this, every click on
    // "Generate link" produced a fresh UUID and a duplicate CR — double
    // clicks reliably bypassed the BE de-dupe. Reset when dialog reopens.
    const [idempotencyKey, setIdempotencyKey] = useState(() => uuidv4());

    // Wrapper that re-mints the idempotency key when the toggle moves
    // AFTER a pre-flight has already produced CRs. Without this, the
    // user going Back → flip → Next would hit the BE with the same
    // ``idempotency_key`` but a different body hash → 409 CONFLICT
    // (see ``SendFormsAPI.create`` idempotency check). Re-minting forces
    // a fresh CR row that picks up the new ``high_fidelity_pdf`` value.
    // The B-4 invariant (one key per click-burst, no duplicate CRs from
    // double-clicks) is preserved because the re-mint only happens on a
    // semantic value change, not on every render.
    const setHighFidelityPdf = useCallback((next) => {
        setHighFidelityPdfRaw((prev) => {
            if (prev !== next) {
                setIdempotencyKey(uuidv4());
            }
            return next;
        });
    }, []);

    // B-7: initialLead → ONE_PATIENT mode hop is the only seed Quick still
    // owns. selectedLead itself is hydrated by the parent (index.jsx) so the
    // value carries across Quick ↔ Advanced switches.
    useEffect(() => {
        if (initialLead) {
            setAudienceMode('ONE_PATIENT');
        }
    }, [initialLead]);

    // Reset wizard-LOCAL state whenever dialog closes. Parent owns the
    // reset of selectedForms / selectedLead / practiceFieldValues so we
    // don't stomp them on view-mode switch.
    useEffect(() => {
        if (!open) {
            const t = setTimeout(() => {
                setStep(1);
                setAudienceMode('ONE_PATIENT');
                setActiveChannel('COPY');
                setGenerating(false);
                setSendResult(null);
                setEmailTemplateId(null);
                setWhatsappTemplateId(null);
                setHighFidelityPdf(true);
                // B-4: mint a fresh key for the next session so reopening the
                // dialog doesn't collide with the previous session's CR.
                setIdempotencyKey(uuidv4());
            }, 200);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [open]);

    // Primary form's form_type drives template segmentation in the
    // engagement-templates query (BE filter: form_type=… is exposed on
    // PracticeEngagementTemplateFilter — practice/filters.py:48).
    const primaryFormType = selectedForms[0]?.form_type || null;

    // ── Data loading ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!open || !practiceId) return;
        dispatch(getConsentForms(practiceId, {
            limit: 200,
            isTemplate: false,
            fields: '{id,name,form_type,description,is_template,is_default,sections{id,name}}',
        }));
    }, [open, practiceId, dispatch]);

    // Templates — refetched whenever the primary form's form_type changes so
    // the dropdown surfaces the template variant that matches what's being
    // sent (e.g. CONSENT templates for a consent form).
    useEffect(() => {
        if (!open || !practiceId) return;
        const emailUrl = primaryFormType
            ? `/api/v1/practices/${practiceId}/engagement-templates/?channel__in=EMAIL&category=FORMS&form_type=${primaryFormType}&use_unlayer=false&ordering=-created_at&limit=100`
            : `/api/v1/practices/${practiceId}/engagement-templates/?channel__in=EMAIL&category=FORMS&use_unlayer=false&ordering=-created_at&limit=100`;
        Promise.all([
            callApi(emailUrl),
            callApi(`/api/v1/me/whatsapp-templates/?practice__in=${practiceId}&usage_type=FORMS&ordering=-created_at&limit=100`),
        ])
            .then(([emailRes, wpRes]) => {
                // Fall back to the unscoped FORMS list if the form_type
                // filter returns nothing — better to show something than
                // an empty Template dropdown.
                if (emailRes?.status === 200) {
                    const results = emailRes.data?.results || [];
                    if (results.length === 0 && primaryFormType) {
                        callApi(`/api/v1/practices/${practiceId}/engagement-templates/?channel__in=EMAIL&category=FORMS&use_unlayer=false&ordering=-created_at&limit=100`)
                            .then((fbRes) => {
                                if (fbRes?.status === 200) setEmailTemplates(fbRes.data?.results || []);
                            })
                            .catch(() => {});
                    } else {
                        setEmailTemplates(results);
                    }
                }
                if (wpRes?.status === 200 && wpRes.data?.results) setWhatsappTemplates(wpRes.data.results);
            })
            .catch(() => {});
    }, [open, practiceId, primaryFormType]);

    // Clear template selections when the primary form's form_type changes —
    // the previous template id may not exist in the new filtered list and
    // would surface as a blank dropdown without a corresponding body.
    useEffect(() => {
        setEmailTemplateId(null);
        setWhatsappTemplateId(null);
    }, [primaryFormType]);

    // Consent / form-URL config — loaded once per dialog open.
    useEffect(() => {
        if (!open || !practiceId) return;

        // Consent / form-URL config for buildFormUrl().
        callApi(`/api/v1/practices/${practiceId}/choices/?ordering=-created_at`)
            .then((res) => {
                if (res?.status === 200 && res.data?.results) {
                    const config = res.data.results.find(
                        (c) => c?.metadata?.type === 'consent_form_config',
                    );
                    setConsentConfig(config || null);
                }
            })
            .catch(() => {});
    }, [open, practiceId, dispatch]);

    // Eagerly load practitioners — they're sourced from the practiceUser
    // slice which is usually already hydrated in the dashboard layout,
    // but if this dialog is opened from a deep link we ensure it's loaded.
    // We don't gate the wizard on it — Step 2 just shows an empty state.

    // ── Step handlers ────────────────────────────────────────────────────────
    // B-7: forms / lead mutations now go through parent-supplied callbacks
    // so state lives at the dialog level and survives Quick ↔ Advanced
    // toggles.
    const handleSelectLead = useCallback((lead) => {
        onSelectLeadProp(lead);
        if (lead) setAudienceMode('ONE_PATIENT');
    }, [onSelectLeadProp]);

    const handleSkipLeadAnonymous = useCallback(() => {
        setAudienceMode('ANONYMOUS');
        onSelectLeadProp(null);
        // Anonymous still supports EMAIL/WHATSAPP via the ad-hoc recipient
        // path (BE-4); we default to COPY here just so the user lands on the
        // link-share view first, but they're free to flip pills.
        setActiveChannel('COPY');
        setStep(4);
        // Fire-and-forget: Step 4 reads `isGeneratingLink` and renders a
        // loading state until the POST resolves. We pass override so the
        // closure doesn't read the stale ONE_PATIENT default from this tick.
        // eslint-disable-next-line no-use-before-define
        generateLink({ overrideAudienceMode: 'ANONYMOUS' });
    }, [onSelectLeadProp]);

    // ── Generate link — POST /consent-requests/send-forms/ ──────────────────
    //
    // Triggered when the user clicks "Generate link" on Step 3 (or via
    // the anonymous escape hatch). Step 4 only delivers the link — by the
    // time we land there it's already in the clipboard.
    const generateLink = async (opts = {}) => {
        if (generating) return null;
        const effectiveAudienceMode = opts.overrideAudienceMode || audienceMode;
        if (effectiveAudienceMode === 'ONE_PATIENT' && !selectedLead) {
            enqueueSnackbar('Pick a patient before generating a link', { variant: 'warning' });
            return null;
        }
        if (!selectedForms.length) {
            enqueueSnackbar('Pick a form before generating a link', { variant: 'warning' });
            return null;
        }

        setGenerating(true);
        try {
            const payload = buildSendFormsPayload({
                audienceMode: effectiveAudienceMode,
                channels: ['COPY_LINK'], // we Generate Link first; Step 4 does the actual delivery.
                selectedForms,
                expiresInHours: Math.max(1, Number(expiryDays) || 7) * 24,
                selectedLead: effectiveAudienceMode === 'ONE_PATIENT' ? selectedLead : null,
                practiceFieldValues,
                idempotencyKey,
                highFidelityPdf,
            });

            const res = await callApi(
                `/api/v1/practices/${practiceId}/consent-requests/send-forms/`,
                'post',
                payload,
            );
            const ok = res?.status === 200 || res?.status === 201 || res?.status === 202;
            if (!ok) {
                const detail = typeof res?.data === 'string'
                    ? res.data
                    : (res?.data?.detail || 'Failed to generate link');
                enqueueSnackbar(detail, { variant: 'error' });
                setGenerating(false);
                return null;
            }

            // Build link list — indexed by form_id with positional fallback.
            //
            // The BE returns a different top-level key by audience_mode:
            //   * ONE_PATIENT / PATIENT_LIST → ``consent_requests: [...]``
            //   * ANONYMOUS                  → ``anonymous_links: [...]``
            // Both rows share the same keys (id, form_id, access_token,
            // form_url, expires_at). Previously this code only read
            // ``consent_requests`` so anonymous pre-flights surfaced as
            // "The pre-flight didn't produce a link." even though the CRs
            // were created server-side. Union both keys here.
            const consentRequests = [
                ...(Array.isArray(res.data?.consent_requests) ? res.data.consent_requests : []),
                ...(Array.isArray(res.data?.anonymous_links) ? res.data.anonymous_links : []),
            ];
            const links = selectedForms.map((form, i) => {
                const cr = consentRequests.find((r) => Number(r?.form_id) === Number(form.id))
                    || consentRequests[i]
                    || null;
                const accessToken = cr?.access_token || null;
                const url = cr?.form_url || buildFormUrl(accessToken, consentConfig);
                // Carry the CR id so the "Send Email" / "Send WhatsApp"
                // step (QuickSendStep4Deliver.handleSendEmail /
                // handleSendWhatsApp) can pass `consent_request_ids` to
                // the BE and trigger the REUSE path at
                // apps/practice/consent_views.py:822-858 instead of
                // re-entering ``_send_forms_one_patient`` and minting
                // duplicate CRs (which is what produced the "4 entries
                // for 1 sequence × 2 channels" duplication bug).
                return {
                    form,
                    accessToken,
                    url,
                    expiresAt: cr?.expires_at || null,
                    consentRequestId: cr?.id || null,
                };
            });

            const firstUrl = links[0]?.url || '';
            const result = {
                formUrl: firstUrl,
                links,
                expiresAt: links.map((l) => l.expiresAt).filter(Boolean).sort()[0] || null,
            };
            setSendResult(result);
            if (typeof onSuccess === 'function') {
                onSuccess({
                    consentRequests: consentRequests
                        .map((cr) => ({ id: cr.id, lead_id: cr.lead_id, status: cr.status }))
                        .filter((cr) => cr.id),
                });
            }

            // Auto-copy the first URL to clipboard so "link is in your
            // clipboard" copy in Step 4 is truthful.
            if (firstUrl) {
                copy(firstUrl);
                enqueueSnackbar('Link generated and copied', { variant: 'success' });
            }

            setGenerating(false);
            return result;
        } catch {
            enqueueSnackbar('Failed to generate link', { variant: 'error' });
            setGenerating(false);
            return null;
        }
    };

    // ── Step validation ──────────────────────────────────────────────────────
    const canAdvance = useMemo(() => {
        if (step === 1) return selectedForms.length > 0;
        if (step === 2) return Boolean(practiceFieldValues?.['system:treated_by']?.value);
        if (step === 3) return audienceMode === 'ONE_PATIENT' ? !!selectedLead : true;
        return true;
    }, [step, selectedForms, practiceFieldValues, selectedLead, audienceMode]);

    const handleNext = async () => {
        if (!canAdvance) return;
        if (step === 3) {
            // Step 4 is now a post-action delivery confirmation: the link
            // must already exist before the user lands there. Previously we
            // only pre-generated for COPY/QR and deferred EMAIL/WHATSAPP to
            // Step 4's Send click — but the CTA still read "Generate link",
            // which broke the contract (users tapped it and Step 4 opened
            // with an empty formUrl in Email mode). Always pre-generate so:
            //   * the URL is universally visible in Step 4
            //   * COPY/QR show the URL immediately
            //   * EMAIL/WHATSAPP Send still hits send-forms but reuses the
            //     same idempotency key so the BE de-dupes the second POST
            //     (B-4 contract) — no duplicate CR.
            const result = await generateLink();
            if (result) setStep(4);
            return;
        }
        setStep((s) => Math.min(TOTAL_STEPS, s + 1));
    };

    const handleBack = () => {
        setStep((s) => Math.max(1, s - 1));
    };

    // ── Render ───────────────────────────────────────────────────────────────
    const formUrl = sendResult?.formUrl || '';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Progress bar — 4 segments */}
            <Box sx={{ px: 2.5, pt: 1, pb: 1.5, display: 'flex', gap: 0.5 }}>
                {[1, 2, 3, 4].map((n) => (
                    <Box
                        key={n}
                        sx={{
                            flex: 1, height: 2, borderRadius: 1,
                            bgcolor: n <= step ? common.mintyZest : 'rgba(145,158,171,0.18)',
                            transition: 'background-color 250ms ease',
                        }}
                    />
                ))}
            </Box>

            {/* Step body */}
            <Box sx={{ px: 2.5, pb: 2, flex: 1, overflowY: 'auto' }}>
                {step === 1 && (
                    <QuickSendStep1Forms
                        forms={forms}
                        formsLoading={formsLoading}
                        selectedForms={selectedForms}
                        onAdd={onAddForm}
                        onRemove={onRemoveForm}
                        onReorder={onReorderForms}
                        practiceId={practiceId}
                    />
                )}
                {step === 2 && (
                    <QuickSendStep2Doctor
                        practiceFieldValues={practiceFieldValues}
                        setPracticeFieldValues={setPracticeFieldValues}
                    />
                )}
                {step === 3 && (
                    <QuickSendStep3Patient
                        practiceId={practiceId}
                        selectedLead={selectedLead}
                        onSelectLead={handleSelectLead}
                        onSkipLeadAnonymous={handleSkipLeadAnonymous}
                        isGeneratingLink={generating}
                        // Toggle lives in Step 3 because the value must be
                        // baked into the pre-flight POST (BE freezes it on
                        // each CR at creation — no PATCH surface exists).
                        // Step 4 just reflects the chosen value read-only.
                        highFidelityPdf={highFidelityPdf}
                        setHighFidelityPdf={setHighFidelityPdf}
                    />
                )}
                {step === 4 && (
                    <QuickSendStep4Deliver
                        practiceId={practiceId}
                        practice={practice}
                        audienceMode={audienceMode}
                        selectedLead={selectedLead}
                        selectedForms={selectedForms}
                        practiceFieldValues={practiceFieldValues}
                        consentConfig={consentConfig}
                        formUrl={formUrl}
                        formLinks={sendResult?.links || []}
                        onSendComplete={setSendResult}
                        // Step 3 → Step 4 always pre-flights, so this flag
                        // is the "creating consent request…" signal during
                        // the transition (and for the retry CTA if the
                        // pre-flight failed).
                        isGeneratingLink={generating}
                        onGenerateLink={() => generateLink({
                            overrideAudienceMode: audienceMode,
                        })}
                        activeChannel={activeChannel}
                        onChannelChange={setActiveChannel}
                        expiryDays={expiryDays}
                        emailTemplates={emailTemplates}
                        whatsappTemplates={whatsappTemplates}
                        emailTemplateId={emailTemplateId}
                        setEmailTemplateId={setEmailTemplateId}
                        whatsappTemplateId={whatsappTemplateId}
                        setWhatsappTemplateId={setWhatsappTemplateId}
                        setSending={setSending}
                        // Read-only after pre-flight: the CR's
                        // `high_fidelity_pdf` is set at creation and is
                        // not patchable today. Step 4 shows the user
                        // their previous choice; the editable toggle
                        // lives in Step 3.
                        highFidelityPdf={highFidelityPdf}
                        // PR-10 item 4 — inline contact-create in Step 4
                        // patches the lead via the existing leads endpoint;
                        // here we bubble the optimistically updated lead
                        // back up so the parent's `selectedLead` reference
                        // flips and downstream consumers (recipient strip,
                        // canSend* gates) re-evaluate.
                        onLeadUpdated={onSelectLeadProp}
                    />
                )}
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    px: 2.5, py: 1.25,
                    borderTop: '1px solid', borderColor: 'divider',
                    bgcolor: '#fafbfc',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}
            >
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                    {(() => {
                        if (step !== 4) return `Step ${step} of ${TOTAL_STEPS}`;
                        // Step 3 → Step 4 always pre-flights, so by the
                        // time we render Step 4 the URL exists and the
                        // clipboard is primed. Email/WhatsApp Send is an
                        // optional follow-on rather than the only path.
                        if (formUrl) return 'Link generated. Share via the channel above.';
                        if (generating) return 'Creating the consent request…';
                        return 'Pre-flight failed — retry from the banner above.';
                    })()}
                </Typography>

                <Stack direction="row" spacing={1}>
                    {step > 1 && (
                        <Button
                            size="small"
                            variant="text"
                            onClick={handleBack}
                            startIcon={<Iconify icon="eva:arrow-back-fill" width={14} />}
                            sx={{
                                textTransform: 'none', fontWeight: 600,
                                color: 'text.secondary', fontSize: '12px',
                            }}
                        >
                            Back
                        </Button>
                    )}
                    {step < 4 && (
                        <Button
                            size="small"
                            variant="contained"
                            disabled={!canAdvance || generating || sending}
                            onClick={handleNext}
                            endIcon={generating
                                ? <CircularProgress size={12} sx={{ color: '#fff' }} />
                                : <Iconify icon="eva:arrow-forward-fill" width={14} />}
                            sx={{
                                bgcolor: common.mintyZest, color: '#fff', textTransform: 'none',
                                fontWeight: 700, borderRadius: '8px', px: 2, py: 0.5,
                                fontSize: '12px',
                                boxShadow: `0 3px 10px ${alpha(common.mintyZest, 0.25)}`,
                                '&:hover': { bgcolor: '#007B40' },
                                '&.Mui-disabled': { bgcolor: 'rgba(145,158,171,0.2)', color: '#919EAB', boxShadow: 'none' },
                            }}
                        >
                            {(() => {
                                if (step === 3) return generating ? 'Generating…' : 'Generate link';
                                return 'Continue';
                            })()}
                        </Button>
                    )}
                    {step === 4 && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={onClose}
                            sx={{
                                textTransform: 'none', fontWeight: 600,
                                borderRadius: '8px', fontSize: '12px',
                                borderColor: 'rgba(145,158,171,0.24)',
                                color: 'text.secondary',
                                '&:hover': { borderColor: 'rgba(145,158,171,0.4)', bgcolor: 'rgba(145,158,171,0.04)' },
                            }}
                        >
                            Done
                        </Button>
                    )}
                </Stack>
            </Box>
        </Box>
    );
};

QuickSend.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    initialLead: PropTypes.object,
    switchToAdvanced: PropTypes.func.isRequired,
    // B-7: lifted state
    selectedForms: PropTypes.array.isRequired,
    onAddForm: PropTypes.func.isRequired,
    onRemoveForm: PropTypes.func.isRequired,
    onReorderForms: PropTypes.func.isRequired,
    selectedLead: PropTypes.object,
    onSelectLead: PropTypes.func.isRequired,
    practiceFieldValues: PropTypes.object,
    setPracticeFieldValues: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
};

QuickSend.defaultProps = {
    initialLead: null,
    selectedLead: null,
    practiceFieldValues: {},
    onSuccess: null,
};

export default QuickSend;
